import type { EmbeddedProduct } from 'modules/supplier/products';
import type { ActivityType } from 'types';
import {
  productToChainPath,
  targetIdFromPath,
  type ChainNode,
  type ChainPath,
} from 'modules/supplier/supplier/components/ProductChainPicker';

type VendorNode = Extract<ChainNode, { kind: 'vendor' }>;
type OutletNode = Extract<ChainNode, { kind: 'outlet' }>;
type ProductNode = Extract<ChainNode, { kind: 'product' }>;
export type MediaProductNode = Omit<ProductNode, 'productKind'> & { productKind: 'MEDIA' };

/** A media discount endpoint and its complete vendor ancestry. */
export type MediaTargetPath =
  | readonly [VendorNode]
  | readonly [VendorNode, OutletNode]
  | readonly [VendorNode, OutletNode, MediaProductNode];

function isMediaProduct(node: ChainNode | undefined): node is MediaProductNode {
  return node?.kind === 'product' && node.productKind === 'MEDIA';
}

/** Narrow a catalogue path to one of the supported media discount target shapes. */
export function asMediaTargetPath(path: ChainPath): MediaTargetPath {
  const [vendor, outlet, product] = path;
  if (path.length === 1 && vendor?.kind === 'vendor') return [vendor];
  if (path.length === 2 && vendor?.kind === 'vendor' && outlet?.kind === 'outlet') return [vendor, outlet];
  if (path.length === 3 && vendor?.kind === 'vendor' && outlet?.kind === 'outlet' && isMediaProduct(product)) {
    return [vendor, outlet, product];
  }
  throw new Error('A media target path must be [vendor], [vendor, outlet], or [vendor, outlet, media product].');
}

/** Validate the ancestry and kind embedded in a discovered media product. */
export function mediaTargetPathFromProduct(product: EmbeddedProduct): MediaTargetPath {
  return asMediaTargetPath(productToChainPath(product));
}

/** Stable endpoint identity, independent of current path labels. */
export function mediaTargetKey(path: MediaTargetPath): string {
  return targetIdFromPath(path);
}

/** Classify structural target drift from the current path returned by the server. */
export function mediaTargetInvalidReason(path: MediaTargetPath, type: ActivityType): string | null {
  const outlet = path.length >= 2 ? path[1] : undefined;
  if (outlet?.type != null && outlet.type !== type) return 'TARGET_TYPE_MISMATCH';
  const product = path.length === 3 ? path[2] : undefined;
  if (product) {
    if (product.productKind !== 'MEDIA') return 'TARGET_NOT_MEDIA';
    if (product.type != null && product.type !== type) return 'TARGET_TYPE_MISMATCH';
  }
  return null;
}

/** Expand complete media paths into unique selectable vendor/outlet/product prefixes. */
export function expandMediaTargetPrefixes(
  paths: readonly ChainPath[],
  { replaceDuplicates = true }: { replaceDuplicates?: boolean } = {},
): MediaTargetPath[] {
  const targets = new Map<string, MediaTargetPath>();
  for (const candidate of paths) {
    const path = asMediaTargetPath(candidate);
    path.forEach((_, index) => {
      const prefix = asMediaTargetPath(path.slice(0, index + 1));
      const key = mediaTargetKey(prefix);
      if (replaceDuplicates || !targets.has(key)) targets.set(key, prefix);
    });
  }
  return [...targets.values()];
}
